/**
 * WebSocket Service for Duel System
 *
 * Manages WebSocket connection lifecycle, message handling, and reconnection logic.
 */

import {
  WebSocketConfig,
  WebSocketConnectionStatus,
  ServerMessage,
  ClientMessage,
  MessageHandler,
  ConnectionHandler,
  ErrorHandler,
} from "types/duels/types";
import { BattleCommand } from "types/battle-engine/commands";

export class DuelWebSocketService {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig;
  private reconnectAttempts = 0;
  private maxReconnectAttempts: number;
  private reconnectDelay: number;
  private heartbeatInterval: number;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private messageQueue: ClientMessage[] = [];

  private messageHandlers: Set<MessageHandler> = new Set();
  private connectionHandlers: Set<ConnectionHandler> = new Set();
  private errorHandlers: Set<ErrorHandler> = new Set();

  private _status: WebSocketConnectionStatus = "disconnected";

  constructor(config: WebSocketConfig) {
    this.config = config;
    this.maxReconnectAttempts = config.reconnectAttempts ?? 5;
    this.reconnectDelay = config.reconnectDelay ?? 1000;
    this.heartbeatInterval = config.heartbeatInterval ?? 30000;
  }

  /**
   * Current connection status
   */
  get status(): WebSocketConnectionStatus {
    return this._status;
  }

  private setStatus(status: WebSocketConnectionStatus) {
    if (this._status !== status) {
      this._status = status;
      this.connectionHandlers.forEach((handler) => handler(status));
    }
  }

  /**
   * Connect to WebSocket server
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          console.log("[WS] Already connected, skipping connection attempt");
          resolve();
          return;
        }

        console.log("[WS] Initiating connection...");
        this.setStatus("connecting");

        // Build WebSocket URL with query parameters
        const url = new URL(this.config.url);
        url.searchParams.set("duel_id", this.config.duel_id);
        url.searchParams.set("player_id", this.config.player_id.toString());
        url.searchParams.set("token", this.config.token);

        this.ws = new WebSocket(url.toString());

        this.ws.onopen = () => {
          console.log("[WS] ✅ Connection OPENED successfully");
          this.setStatus("connected");
          this.reconnectAttempts = 0;
          this.startHeartbeat();

          // Request initial state immediately
          this.send({
            action: "get_state",
            payload: {
              duel_id: this.config.duel_id,
            },
          });

          this.flushMessageQueue();
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(event.data);
        };

        this.ws.onerror = (error) => {
          console.error("[WS] ❌ ERROR occurred:", error);
          console.error("[WS] Error details:", {
            readyState: this.ws?.readyState,
            status: this._status,
            reconnectAttempts: this.reconnectAttempts,
          });
          this.errorHandlers.forEach((handler) =>
            handler(new Error("WebSocket connection error")),
          );
        };

        this.ws.onclose = (event) => {
          console.log("[WS] 🔌 Connection CLOSED");

          this.stopHeartbeat();
          this.setStatus("disconnected");

          // Attempt reconnection if not a clean close
          if (
            event.code !== 1000 &&
            this.reconnectAttempts < this.maxReconnectAttempts
          ) {
            console.log("[WS] Will attempt reconnection (not a clean close)");
            this.reconnect();
          } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error("[WS] Max reconnection attempts reached, giving up");
            this.errorHandlers.forEach((handler) =>
              handler(new Error("Max reconnection attempts reached")),
            );
            reject(new Error("Max reconnection attempts reached"));
          } else {
            console.log("[WS] Clean close (code 1000), not reconnecting");
          }
        };

        // Timeout for connection
        setTimeout(() => {
          if (this._status === "connecting") {
            console.error("[WS] ⏱️ Connection TIMEOUT after 10 seconds");
            reject(new Error("Connection timeout"));
            this.disconnect();
          }
        }, 10000);
      } catch (error) {
        console.error("[WS] Exception during connection setup:", error);
        this.setStatus("disconnected");
        reject(error);
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    console.log("[WS] Disconnect called");
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      console.log("[WS] Closing WebSocket connection (code 1000)");
      this.ws.close(1000, "Client disconnect");
      this.ws = null;
    }

    this.setStatus("disconnected");
  }

  /**
   * Reconnect with exponential backoff
   */
  private reconnect() {
    if (this.reconnectTimer) {
      console.log("[WS] Reconnect already scheduled, skipping");
      return;
    }

    this.setStatus("reconnecting");
    this.reconnectAttempts++;

    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      8000,
    );

    console.log(
      `[WS] 🔄 Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      console.log("[WS] Executing reconnection attempt...");
      this.connect().catch((error) => {
        console.error("[WS] Reconnection failed:", error);
      });
    }, delay);
  }

  /**
   * Send a message to the server
   */
  send(message: ClientMessage) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const messageStr = JSON.stringify(message);
      console.log("[WS] 📤 Sending message:", message.action, message);
      this.ws.send(messageStr);
    } else {
      // Queue message for later delivery
      console.log(
        "[WS] ⏸️ WebSocket not ready (readyState:",
        this.ws?.readyState,
        "), queuing message:",
        message.action,
      );
      this.messageQueue.push(message);
    }
  }

  /**
   * Send player action (pick or ban)
   */
  sendPlayerAction(
    duel_id: string,
    step_index: number,
    action_type: "pick" | "ban",
    pokemon_ids: number[],
  ) {
    this.send({
      action: "player_action",
      payload: {
        duel_id,
        step_index,
        action_type,
        pokemon_ids,
      },
    });
  }

  /**
   * Send visual acknowledgment
   */
  sendVisualAck(duel_id: string, round_index: number) {
    this.send({
      action: "visual_ack",
      payload: {
        duel_id,
        round_index,
      },
    });
  }

  /**
   * Request a fresh `get_state` response (e.g. `battle_snapshot` for an
   * active live room — used to spawn the live scene, both on fresh start
   * and reconnect).
   */
  requestState(duel_id: string) {
    this.send({ action: "get_state", payload: { duel_id } });
  }

  /**
   * Request a draft-only, once-per-round hand reroll.
   */
  sendReroll(duel_id: string) {
    this.send({ action: "reroll", payload: { duel_id } });
  }

  /**
   * Send a live battle command (focus/play_item/etc — F3), forwarded as-is
   * to the Fargate battle-server by `ws-player-action`.
   */
  sendBattleCommand(duel_id: string, command: BattleCommand) {
    this.send({
      action: "battle_command",
      payload: { duel_id, command },
    });
  }

  /**
   * Send heartbeat
   */
  private sendHeartbeat() {
    console.log("[WS] 💓 Sending heartbeat");
    this.send({
      action: "heartbeat",
    });
  }

  /**
   * Handle incoming message from server
   */
  private handleMessage(data: string) {
    try {
      const message: ServerMessage = JSON.parse(data);
      console.log("Received message:", message.type, message);

      this.messageHandlers.forEach((handler) => handler(message));
    } catch (error) {
      console.error("[WS] ❌ Error parsing message:", error, "Raw data:", data);
      this.errorHandlers.forEach((handler) =>
        handler(new Error("Failed to parse server message")),
      );
    }
  }

  /**
   * Flush queued messages
   */
  private flushMessageQueue() {
    if (this.messageQueue.length > 0) {
      console.log(
        `[WS] 📦 Flushing ${this.messageQueue.length} queued messages`,
      );
    }
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      if (message) {
        this.send(message);
      }
    }
  }

  /**
   * Start heartbeat timer
   */
  private startHeartbeat() {
    this.stopHeartbeat();
    console.log(
      `[WS] ❤️ Starting heartbeat (interval: ${this.heartbeatInterval}ms)`,
    );
    this.heartbeatTimer = setInterval(() => {
      this.sendHeartbeat();
    }, this.heartbeatInterval);
  }

  /**
   * Stop heartbeat timer
   */
  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      console.log("[WS] Stopping heartbeat");
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Register message handler
   */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  /**
   * Register connection status handler
   */
  onConnectionChange(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler);
    return () => this.connectionHandlers.delete(handler);
  }

  /**
   * Register error handler
   */
  onError(handler: ErrorHandler): () => void {
    this.errorHandlers.add(handler);
    return () => this.errorHandlers.delete(handler);
  }

  /**
   * Remove all handlers and disconnect
   */
  destroy() {
    console.log("[WS] Destroying service");
    this.disconnect();
    this.messageHandlers.clear();
    this.connectionHandlers.clear();
    this.errorHandlers.clear();
  }
}
